"use client";

import * as React from "react";

import Spinner from "@repo/ui/components/Extras/common/spinner";
import { Checkbox } from "@repo/ui/components/ui/checkbox";
import { Label } from "@repo/ui/components/ui/label";
import { useFormatPrice } from "@repo/ui/Hooks/useFormatPrice";
import type { CommonInfoSection } from "@repo/ui/types/cms";

type ExtrasBoardingPassOptionItemCardProps = {
  id: string;
  title: string;
  inBundle?: boolean;
  price: number;
  checked: boolean;
  onChangeAction: (checked: boolean, ssrKey?: string) => void;
  disabled: boolean;
  commonInfo?: CommonInfoSection;
  ssrKey?: string;
  loading?: boolean;
};

export default function ExtrasBoardingPassOptionItemCard({
  id,
  title,
  inBundle = false,
  price,
  checked,
  onChangeAction,
  disabled,
  commonInfo,
  ssrKey,
  loading = false,
}: Readonly<ExtrasBoardingPassOptionItemCardProps>) {
  const formatPrice = useFormatPrice();
  const priceLabel =
    (inBundle && price == 0) || price == 0
      ? (commonInfo?.includedLabel ?? "Incluido")
      : `${formatPrice(price)}`;

  const onCardKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>) => {
    if (disabled) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onChangeAction(!checked);
    }
  };

  return (
    <Label
      htmlFor={id}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={onCardKeyDown}
      aria-disabled={disabled || undefined}
      className={`block w-full max-w-full select-none space-y-4 rounded-xl border bg-white p-5 transition-all duration-300 ease-in-out ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:shadow-xl"
      }`}
      data-testid={`${id}-section-lbl`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-medium text-brand-blue">
          <div className="relative flex items-center justify-center">
            <Checkbox
              id={id}
              checked={checked && !loading}
              onCheckedChange={(v) => onChangeAction(!!v, ssrKey)}
              disabled={disabled || loading}
              className={`
                h-6 w-6 rounded-full border border-brand-cyan
                data-[state=checked]:bg-brand-cyan data-[state=checked]:border-brand-cyan
                data-[state=checked]:text-white
    `}
            />
            {loading && <Spinner colorClassName="h-5 w-5" />}
          </div>

          {title && <span className="wrap-break-word">{title}</span>}
        </div>

        <div className="text-sm font-semibold whitespace-nowrap text-brand-blue">{priceLabel}</div>
      </div>
    </Label>
  );
}
